import { useRef, type KeyboardEvent } from "react";
import { useTranslation } from "react-i18next";

/**
 * apps/web :: components/exam :: ExamQuestionNavigator
 *
 * Dot-row navigator that lets the user jump between exam questions and
 * see, at a glance, which ones they've answered. Modelled as a
 * `role="tablist"` (each dot is a `role="tab"`) to match the
 * StateSwitcher / ChoiceList a11y pattern already in this app.
 *
 * Keyboard:
 *
 *   ArrowLeft / ArrowRight — previous / next (wraps)
 *   Home / End             — first / last
 *   Enter / Space          — select current (no-op visually because the
 *                            button's default click handler already fires)
 *
 * The parent owns `currentIndex` and receives `onJump(index)` on every
 * navigation — the component never mutates index itself.
 */
export interface ExamQuestionNavigatorProps {
  questionCount: number;
  /** Keyed by question INDEX (0-based). true = user has an answer stored. */
  answered: Record<number, boolean>;
  currentIndex: number;
  onJump: (index: number) => void;
}

export default function ExamQuestionNavigator({
  questionCount,
  answered,
  currentIndex,
  onJump,
}: ExamQuestionNavigatorProps) {
  const { t } = useTranslation();
  const btnRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  function focusAt(index: number) {
    if (questionCount === 0) return;
    const safe = ((index % questionCount) + questionCount) % questionCount;
    btnRefs.current[safe]?.focus();
    onJump(safe);
  }

  function handleKey(ev: KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (ev.key) {
      case "ArrowRight":
      case "ArrowDown":
        ev.preventDefault();
        focusAt(index + 1);
        return;
      case "ArrowLeft":
      case "ArrowUp":
        ev.preventDefault();
        focusAt(index - 1);
        return;
      case "Home":
        ev.preventDefault();
        focusAt(0);
        return;
      case "End":
        ev.preventDefault();
        focusAt(questionCount - 1);
        return;
      default:
        return;
    }
  }

  const dots = Array.from({ length: questionCount }, (_, i) => i);

  return (
    <nav
      role="tablist"
      aria-label={t("exam.navigator.ariaLabel")}
      data-testid="exam-question-navigator"
      className="flex flex-wrap gap-xs"
    >
      {dots.map((index) => {
        const isCurrent = index === currentIndex;
        const isAnswered = answered[index] === true;
        const className = [
          "inline-flex h-8 w-8 items-center justify-center rounded-full border text-sm font-semibold transition-colors",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
          isCurrent
            ? "border-brand-primary bg-brand-primary text-white"
            : isAnswered
              ? "border-brand-primary bg-brand-primary/10 text-brand-primary"
              : "border-brand-primary/30 bg-white text-brand-primary",
        ].join(" ");
        return (
          <button
            key={index}
            ref={(el) => {
              btnRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            aria-current={isCurrent ? "true" : undefined}
            aria-label={t("exam.navigator.dotAriaLabel", {
              index: index + 1,
              total: questionCount,
              status: isAnswered
                ? t("exam.navigator.answered")
                : t("exam.navigator.unanswered"),
            })}
            data-testid={`exam-nav-dot-${index}`}
            data-answered={isAnswered ? "true" : "false"}
            tabIndex={isCurrent ? 0 : -1}
            className={className}
            onClick={() => onJump(index)}
            onKeyDown={(ev) => handleKey(ev, index)}
          >
            {index + 1}
          </button>
        );
      })}
    </nav>
  );
}
